import { createDefaultFilterState, presets } from "./filter-config.js";
import {
  filterAndRankAttractions,
  getActiveFilterChips,
  getRelaxSuggestions,
  parseFilterState,
  removeFilter,
  serializeFilterState,
} from "./filter-engine.js";
import { renderApp, renderDetailDialog, renderResults } from "./templates.js";
import type {
  BookingPolicy,
  CostLevel,
  DimensionKey,
  FilterState,
  MobilityLimit,
  NeighbourhoodCharacter,
  Season,
  SortOption,
  TimeOfDay,
  TransportMode,
  TravelContentEntry,
  TravellerType,
  WaterRelationship,
} from "./types.js";

const appElement = document.querySelector<HTMLDivElement>("#app");
if (!appElement) throw new Error("The #app root element is missing.");
const app: HTMLDivElement = appElement;

let attractions: TravelContentEntry[] = [];
let state: FilterState = parseFilterState(window.location.search);
let searchTimer: number | undefined;

void bootstrap();

async function bootstrap(): Promise<void> {
  try {
    const response = await fetch("/data/attractions.json", { cache: "no-store" });
    if (!response.ok) throw new Error(`Could not load attractions (${response.status}).`);

    const data: unknown = await response.json();
    if (!Array.isArray(data) || !data.every(isTravelContentEntry)) {
      throw new Error("The attractions data does not contain valid top-level travel content entries.");
    }

    attractions = data;
    renderAll();
    bindGlobalEvents();
  } catch (error) {
    const message = error instanceof Error ? error.message : "Unbekannter Ladefehler";
    app.innerHTML = renderLoadError(message);
  }
}

function isTravelContentEntry(value: unknown): value is TravelContentEntry {
  if (!value || typeof value !== "object") return false;
  const entry = value as Partial<TravelContentEntry>;
  return (
    typeof entry.id === "string" &&
    typeof entry.title === "string" &&
    entry.language === "de" &&
    typeof entry.contentType === "string" &&
    Boolean(entry.destination) &&
    Boolean(entry.intent) &&
    Boolean(entry.audience) &&
    Boolean(entry.content) &&
    Boolean(entry.experienceProfile) &&
    Boolean(entry.ratings) &&
    Boolean(entry.seo) &&
    Boolean(entry.kpis) &&
    typeof entry.status === "string"
  );
}

function renderAll(): void {
  const ranked = filterAndRankAttractions(attractions, state);
  const chips = getActiveFilterChips(state);
  const relaxSuggestions = ranked.length === 0 ? getRelaxSuggestions(attractions, state) : [];
  app.innerHTML = renderApp(state, ranked, attractions.length, chips, relaxSuggestions);
  syncControlsFromState();
  syncUrl();
}

function updateDynamicRegions(): void {
  const ranked = filterAndRankAttractions(attractions, state);
  const chips = getActiveFilterChips(state);
  const relaxSuggestions = ranked.length === 0 ? getRelaxSuggestions(attractions, state) : [];
  const resultsRegion = document.querySelector<HTMLElement>("#results-region");

  if (resultsRegion) {
    resultsRegion.innerHTML = renderResults(
      state,
      ranked,
      attractions.length,
      chips,
      relaxSuggestions,
    );
  }

  document.querySelectorAll<HTMLElement>("[data-filter-count-badge]").forEach((badge) => {
    badge.textContent = String(chips.length);
    badge.classList.toggle("hidden", chips.length === 0);
  });

  document.querySelectorAll<HTMLElement>("[data-mobile-filter-count]").forEach((badge) => {
    badge.textContent = `(${chips.length})`;
  });

  syncControlsFromState();
  syncAccordionCounts();
  syncPresetHighlight();
  syncUrl();
}

function bindGlobalEvents(): void {
  document.addEventListener("click", handleClick);
  document.addEventListener("input", handleInput);
  document.addEventListener("change", handleChange);
  document.addEventListener("error", handleImageError, true);
  window.addEventListener("popstate", () => {
    state = parseFilterState(window.location.search);
    renderAll();
  });
}

function handleImageError(event: Event): void {
  const image = event.target;
  if (!(image instanceof HTMLImageElement) || !image.matches("[data-attraction-image]")) return;

  const fallback = image.dataset.imageFallback;
  if (!fallback || image.dataset.fallbackApplied === "true") return;

  image.dataset.fallbackApplied = "true";
  image.alt = "";
  image.src = fallback;
}

function handleClick(event: MouseEvent): void {
  const target = event.target as Element | null;
  if (!target) return;

  if (target instanceof HTMLDialogElement && target.open) {
    target.close();
    return;
  }

  const presetButton = target.closest<HTMLElement>("[data-preset]");
  if (presetButton) {
    const preset = presets.find((candidate) => candidate.id === presetButton.dataset.preset);
    if (!preset) return;
    state = preset.apply();
    renderAll();
    focusResultsHeading(false);
    return;
  }

  const removeButton = target.closest<HTMLElement>("[data-remove-filter]");
  if (removeButton?.dataset.removeFilter) {
    state = removeFilter(state, removeButton.dataset.removeFilter);
    updateDynamicRegions();
    return;
  }

  const actionElement = target.closest<HTMLElement>("[data-action]");
  const action = actionElement?.dataset.action;
  if (!action) return;

  switch (action) {
    case "reset-filters":
      state = createDefaultFilterState();
      renderAll();
      focusResultsHeading(false);
      break;
    case "open-mobile-filters":
      getDialog("filter-dialog")?.showModal();
      break;
    case "close-mobile-filters":
      getDialog("filter-dialog")?.close();
      break;
    case "apply-mobile-filters":
      getDialog("filter-dialog")?.close();
      focusResultsHeading(true);
      break;
    case "open-detail": {
      const attractionId = actionElement.dataset.attractionId;
      const attraction = attractions.find((candidate) => candidate.id === attractionId);
      const dialog = getDialog("detail-dialog");
      const content = document.querySelector<HTMLElement>("#detail-dialog-content");
      if (!attraction || !dialog || !content) return;
      content.innerHTML = renderDetailDialog(attraction);
      dialog.showModal();
      break;
    }
    case "close-detail":
      getDialog("detail-dialog")?.close();
      break;
  }
}

function handleInput(event: Event): void {
  const input = event.target as HTMLInputElement | null;
  if (!input?.dataset.filter) return;

  switch (input.dataset.filter) {
    case "query":
      state.query = input.value;
      state.activePreset = null;
      window.clearTimeout(searchTimer);
      searchTimer = window.setTimeout(updateDynamicRegions, 120);
      break;
    case "score": {
      const key = input.dataset.scoreKey as DimensionKey | undefined;
      if (!key) return;
      state.minScores[key] = Number(input.value);
      state.activePreset = null;
      updateScoreOutputs(key, state.minScores[key]);
      updateDynamicRegions();
      break;
    }
    case "indoor":
      state.minIndoorPercentage = Number(input.value);
      state.activePreset = null;
      updateIndoorOutputs(state.minIndoorPercentage);
      updateDynamicRegions();
      break;
    case "stroller":
      state.minStrollerScore = Number(input.value);
      state.activePreset = null;
      updateStrollerOutputs(state.minStrollerScore);
      updateDynamicRegions();
      break;
    case "child-age":
      state.childAge = parseOptionalInteger(input.value, 0, 17);
      state.activePreset = null;
      updateDynamicRegions();
      break;
  }
}

function handleChange(event: Event): void {
  const control = event.target as HTMLInputElement | HTMLSelectElement | null;
  const filter = control?.dataset.filter;
  if (!control || !filter) return;

  if (filter !== "sort") state.activePreset = null;

  switch (filter) {
    case "traveller":
      state.travellerTypes = updateSelection(
        state.travellerTypes,
        control.value as TravellerType,
        (control as HTMLInputElement).checked,
      );
      break;
    case "season":
      state.seasons = updateSelection(
        state.seasons,
        control.value as Season,
        (control as HTMLInputElement).checked,
      );
      break;
    case "time":
      state.timesOfDay = updateSelection(
        state.timesOfDay,
        control.value as TimeOfDay,
        (control as HTMLInputElement).checked,
      );
      break;
    case "transport":
      state.transportModes = updateSelection(
        state.transportModes,
        control.value as TransportMode,
        (control as HTMLInputElement).checked,
      );
      break;
    case "character":
      state.neighbourhoodCharacters = updateSelection(
        state.neighbourhoodCharacters,
        control.value as NeighbourhoodCharacter,
        (control as HTMLInputElement).checked,
      );
      break;
    case "water":
      state.waterRelationships = updateSelection(
        state.waterRelationships,
        control.value as WaterRelationship,
        (control as HTMLInputElement).checked,
      );
      break;
    case "cost":
      state.maxCostLevel = control.value as CostLevel | "any";
      break;
    case "price":
      state.maxPriceSek = parseOptionalNumber(control.value);
      break;
    case "duration":
      state.maxDurationMinutes = parseOptionalNumber(control.value);
      break;
    case "travel":
      state.maxTravelTimeMinutes = parseOptionalNumber(control.value);
      break;
    case "booking":
      state.bookingPolicy = control.value as BookingPolicy;
      break;
    case "mobility":
      state.maxMobility = control.value as MobilityLimit;
      break;
    case "effort":
      state.maxPhysicalEffort = parseOptionalNumber(control.value);
      break;
    case "rain-safe":
      state.rainSafeOnly = (control as HTMLInputElement).checked;
      break;
    case "children":
      state.childrenOnly = (control as HTMLInputElement).checked;
      break;
    case "car-free":
      state.carFreeOnly = (control as HTMLInputElement).checked;
      break;
    case "quiet":
      state.quietOnly = (control as HTMLInputElement).checked;
      break;
    case "toilets":
      state.toiletsOnly = (control as HTMLInputElement).checked;
      break;
    case "sort":
      state.sortBy = control.value as SortOption;
      break;
    default:
      return;
  }

  updateDynamicRegions();
}

function updateSelection<T extends string>(values: T[], value: T, checked: boolean): T[] {
  return checked
    ? Array.from(new Set([...values, value]))
    : values.filter((candidate) => candidate !== value);
}

function parseOptionalNumber(value: string): number | null {
  if (value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function parseOptionalInteger(value: string, minimum: number, maximum: number): number | null {
  if (value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= minimum && parsed <= maximum ? parsed : null;
}

function syncControlsFromState(): void {
  document.querySelectorAll<HTMLInputElement>('[data-filter="query"]').forEach((input) => {
    if (input.value !== state.query) input.value = state.query;
  });

  document.querySelectorAll<HTMLInputElement>('[data-filter="score"]').forEach((input) => {
    const key = input.dataset.scoreKey as DimensionKey | undefined;
    if (key) input.value = String(state.minScores[key]);
  });

  syncCheckboxGroup("traveller", state.travellerTypes);
  syncCheckboxGroup("season", state.seasons);
  syncCheckboxGroup("time", state.timesOfDay);
  syncCheckboxGroup("transport", state.transportModes);
  syncCheckboxGroup("character", state.neighbourhoodCharacters);
  syncCheckboxGroup("water", state.waterRelationships);

  syncSelect("cost", state.maxCostLevel);
  syncSelect("price", state.maxPriceSek?.toString() ?? "");
  syncSelect("duration", state.maxDurationMinutes?.toString() ?? "");
  syncSelect("travel", state.maxTravelTimeMinutes?.toString() ?? "");
  syncSelect("booking", state.bookingPolicy);
  syncSelect("mobility", state.maxMobility);
  syncSelect("effort", state.maxPhysicalEffort?.toString() ?? "");
  syncSelect("sort", state.sortBy);

  syncCheckbox("rain-safe", state.rainSafeOnly);
  syncCheckbox("children", state.childrenOnly);
  syncCheckbox("car-free", state.carFreeOnly);
  syncCheckbox("quiet", state.quietOnly);
  syncCheckbox("toilets", state.toiletsOnly);

  document.querySelectorAll<HTMLInputElement>('[data-filter="child-age"]').forEach((input) => {
    const value = state.childAge?.toString() ?? "";
    if (input.value !== value) input.value = value;
  });
  document.querySelectorAll<HTMLInputElement>('[data-filter="indoor"]').forEach((input) => {
    input.value = String(state.minIndoorPercentage);
  });
  document.querySelectorAll<HTMLInputElement>('[data-filter="stroller"]').forEach((input) => {
    input.value = String(state.minStrollerScore);
  });

  for (const [key, value] of Object.entries(state.minScores) as Array<[DimensionKey, number]>) {
    updateScoreOutputs(key, value);
  }
  updateIndoorOutputs(state.minIndoorPercentage);
  updateStrollerOutputs(state.minStrollerScore);
  syncPresetHighlight();
}

function syncCheckboxGroup<T extends string>(filter: string, selected: T[]): void {
  document.querySelectorAll<HTMLInputElement>(`input[data-filter="${filter}"]`).forEach((input) => {
    input.checked = selected.includes(input.value as T);
  });
}

function syncCheckbox(filter: string, checked: boolean): void {
  document.querySelectorAll<HTMLInputElement>(`input[data-filter="${filter}"]`).forEach((input) => {
    input.checked = checked;
  });
}

function syncSelect(filter: string, value: string): void {
  document.querySelectorAll<HTMLSelectElement>(`select[data-filter="${filter}"]`).forEach((select) => {
    select.value = value;
  });
}

function updateScoreOutputs(key: DimensionKey, value: number): void {
  document.querySelectorAll<HTMLOutputElement>(`[data-score-output="${key}"]`).forEach((output) => {
    output.textContent = value === 0 ? "egal" : `ab ${value}`;
    output.classList.toggle("text-teal-800", value > 0);
    output.classList.toggle("text-slate-400", value === 0);
    output.classList.toggle("shadow-sm", value > 0);
  });
}

function updateIndoorOutputs(value: number): void {
  document.querySelectorAll<HTMLOutputElement>('[data-output="indoor"]').forEach((output) => {
    output.textContent = value === 0 ? "egal" : `${value}%`;
  });
}

function updateStrollerOutputs(value: number): void {
  document.querySelectorAll<HTMLOutputElement>('[data-output="stroller"]').forEach((output) => {
    output.textContent = value === 0 ? "egal" : `ab ${value}`;
  });
}

function syncAccordionCounts(): void {
  const counts: Record<string, number> = {
    primary: Object.entries(state.minScores).filter(([key, value]) =>
      ["sunny", "rain", "history", "atmosphere", "walking", "water", "localness", "children", "value"].includes(key) && value > 0,
    ).length,
    secondary: Object.entries(state.minScores).filter(([key, value]) =>
      ["food", "photography", "spontaneity", "experienceDensity", "cold", "culture", "nature", "romance", "architecture", "accessibility", "timeEfficiency"].includes(key) && value > 0,
    ).length,
    traveller: state.travellerTypes.length,
    budget: [state.maxCostLevel !== "any", state.maxPriceSek !== null, state.maxDurationMinutes !== null, state.maxTravelTimeMinutes !== null].filter(Boolean).length,
    weather: [state.minIndoorPercentage > 0, state.rainSafeOnly, state.seasons.length > 0, state.timesOfDay.length > 0].filter(Boolean).length,
    planning: [state.bookingPolicy !== "any", state.maxMobility !== "any", state.childrenOnly, state.childAge !== null, state.carFreeOnly, state.quietOnly, state.toiletsOnly, state.minStrollerScore > 0, state.maxPhysicalEffort !== null].filter(Boolean).length,
    place: state.transportModes.length + state.neighbourhoodCharacters.length + state.waterRelationships.length,
  };

  for (const [section, count] of Object.entries(counts)) {
    document.querySelectorAll<HTMLElement>(`[data-section-count="${section}"]`).forEach((badge) => {
      badge.textContent = String(count);
      badge.classList.toggle("hidden", count === 0);
    });
  }
}

function syncPresetHighlight(): void {
  document.querySelectorAll<HTMLElement>("[data-preset]").forEach((button) => {
    const active = state.activePreset !== null && button.dataset.preset === state.activePreset;
    button.setAttribute("aria-pressed", String(active));
    button.classList.toggle("ring-2", active);
    button.classList.toggle("ring-teal-700", active);
    button.classList.toggle("ring-offset-2", active);
  });
}

function focusResultsHeading(scroll: boolean): void {
  window.requestAnimationFrame(() => {
    const heading = document.querySelector<HTMLElement>("#results-heading");
    heading?.focus({ preventScroll: !scroll });
    if (scroll) heading?.scrollIntoView({ behavior: "smooth", block: "start" });
  });
}

function syncUrl(): void {
  window.history.replaceState(null, "", serializeFilterState(state));
}

function getDialog(id: string): HTMLDialogElement | null {
  return document.querySelector<HTMLDialogElement>(`#${id}`);
}

function renderLoadError(message: string): string {
  return `
    <main class="grid min-h-screen place-items-center bg-sand-50 p-6 text-center text-slate-800">
      <div class="max-w-lg rounded-3xl border border-coral-200 bg-white p-8 shadow-xl">
        <p class="text-sm font-black uppercase tracking-[0.16em] text-coral-700">Daten konnten nicht geladen werden</p>
        <h1 class="mt-3 text-2xl font-black">Bitte starte die Seite über den lokalen Entwicklungsserver.</h1>
        <p class="mt-3 text-sm leading-6 text-slate-600">${escapeForError(message)}</p>
        <code class="mt-5 block rounded-xl bg-slate-950 px-4 py-3 text-left text-sm text-teal-100">npm install\nnpm run dev</code>
      </div>
    </main>
  `;
}

function escapeForError(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}
